/**
 * Cart & checkout UI enhancements.
 *
 * Progressive enhancement only — WooCommerce remains fully functional without
 * this script. We wrap the native quantity input with accessible +/- steppers
 * and dispatch the events WooCommerce listens for, so the "Update cart" button
 * activates exactly as it would on manual entry.
 */

const STEP_LABELS = {
  increase: 'افزایش تعداد',
  decrease: 'کاهش تعداد',
};

function buildStepper(quantityEl) {
  if (quantityEl.dataset.chrzQty === 'ready') return;

  const input = quantityEl.querySelector('input.qty');
  if (!input) return;

  quantityEl.dataset.chrzQty = 'ready';
  quantityEl.classList.add('chrz-qty');

  const makeBtn = (dir, glyph) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'chrz-qty__btn';
    btn.dataset.dir = dir;
    btn.textContent = glyph;
    btn.setAttribute('aria-label', STEP_LABELS[dir === 'up' ? 'increase' : 'decrease']);
    btn.tabIndex = -1; // the input itself remains the keyboard target
    return btn;
  };

  const minus = makeBtn('down', '−');
  const plus = makeBtn('up', '+');

  // Logical order: WooCommerce markup is [input]; we frame it as [− input +].
  quantityEl.insertBefore(minus, input);
  quantityEl.appendChild(plus);

  const step = (delta) => {
    const stepAttr = parseFloat(input.getAttribute('step')) || 1;
    const min = input.getAttribute('min') !== '' ? parseFloat(input.getAttribute('min')) : null;
    const max = input.getAttribute('max') !== '' ? parseFloat(input.getAttribute('max')) : null;
    let next = (parseFloat(input.value) || 0) + delta * stepAttr;

    if (min !== null && !Number.isNaN(min)) next = Math.max(min, next);
    if (max !== null && !Number.isNaN(max)) next = Math.min(max, next);

    if (next === parseFloat(input.value)) return;

    input.value = next;
    // Fire the events WooCommerce / jQuery bind to so totals + update button react.
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  minus.addEventListener('click', () => step(-1));
  plus.addEventListener('click', () => step(1));
}

function initQuantitySteppers(root = document) {
  root
    .querySelectorAll('.woocommerce-cart-form .quantity:not([data-chrz-qty])')
    .forEach(buildStepper);
}

// ─── Coupon bar (checkout summary) ────────────────────────────────────────────
// The bar is a plain div (not a <form>) because it sits inside the checkout form.
// Apply the code via WooCommerce's apply_coupon AJAX endpoint, then refresh the
// order review so the discount + new total appear.
function initCouponBar() {
  const root = document.querySelector('[data-chrz-coupon]');
  if (!root || root.dataset.chrzReady === '1') return;
  root.dataset.chrzReady = '1';

  const input = root.querySelector('.chrz-coupon__input');
  const btn = root.querySelector('[data-chrz-apply-coupon]');
  const msg = document.querySelector('.chrz-coupon__msg');
  if (!input || !btn) return;

  const setLocked = (locked) => {
    input.disabled = locked;
    btn.disabled = locked;
    root.dataset.hasCoupon = locked ? '1' : '0';
  };

  setLocked(root.dataset.hasCoupon === '1');

  const ajaxUrl = (
    (window.wc_checkout_params && window.wc_checkout_params.wc_ajax_url) ||
    '/?wc-ajax=%%endpoint%%'
  ).replace('%%endpoint%%', 'apply_coupon');

  async function apply() {
    const code = input.value.trim();
    if (!code) {
      input.focus();
      return;
    }

    btn.disabled = true;
    if (msg) msg.innerHTML = '';

    try {
      const res = await fetch(ajaxUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          security: root.dataset.nonce || '',
          coupon_code: code,
        }),
      });
      const html = await res.text();
      if (msg) msg.innerHTML = html; // WooCommerce returns styled notice markup
      const rejected =
        html.includes('woocommerce-error') ||
        html.includes('is-error') ||
        html.includes('coupon-error-notice');
      if (!rejected && html.trim() !== '') setLocked(true);
      input.value = '';
      if (window.jQuery) window.jQuery(document.body).trigger('update_checkout');
    } catch {
      if (msg) {
        msg.textContent = (window.Chrz && window.Chrz.i18n && window.Chrz.i18n.error) || 'خطا';
      }
    } finally {
      btn.disabled = root.dataset.hasCoupon === '1';
    }
  }

  btn.addEventListener('click', apply);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      apply();
    }
  });

  if (window.jQuery) {
    window
      .jQuery(document.body)
      .on('removed_coupon removed_coupon_in_checkout', () => setLocked(false));
  }
}

// ─── "Redirecting to gateway" overlay ────────────────────────────────────────
// Placing the order waits on the payment gateway's request API (a few seconds
// to mint the redirect URL). The theme hides WooCommerce's own blockUI spinner
// (too clumsy for small qty/coupon AJAX), so without this the final submit looks
// frozen — "nothing happens" — until the redirect. Show a clear branded overlay
// for that step and remove it if the checkout comes back with an error.
let redirectOverlayEl = null;

function showCheckoutRedirectOverlay() {
  if (redirectOverlayEl) return;
  redirectOverlayEl = document.createElement('div');
  redirectOverlayEl.className = 'chrz-checkout-redirect';
  redirectOverlayEl.setAttribute('role', 'status');
  redirectOverlayEl.setAttribute('aria-live', 'polite');
  redirectOverlayEl.innerHTML =
    '<div class="chrz-checkout-redirect__box">' +
    '<span class="chrz-checkout-redirect__spinner" aria-hidden="true"></span>' +
    '<span class="chrz-checkout-redirect__text">در حال انتقال به درگاه پرداخت…</span>' +
    '</div>';
  document.body.appendChild(redirectOverlayEl);
}

function hideCheckoutRedirectOverlay() {
  if (!redirectOverlayEl) return;
  redirectOverlayEl.remove();
  redirectOverlayEl = null;
}

function initCheckoutSubmitFeedback() {
  if (!window.jQuery) return;
  const $form = window.jQuery('form.woocommerce-checkout');
  if (!$form.length) return;

  // Logged-in shoppers submit the order directly (no OTP step), so any
  // place-order attempt that passes validation goes straight to the gateway —
  // show the overlay immediately. (Guests show it post-verification below.)
  if (document.body.classList.contains('logged-in')) {
    $form.on('checkout_place_order', () => {
      showCheckoutRedirectOverlay();
      return true;
    });
  }

  // WooCommerce fires this on the body when the AJAX checkout returns an error
  // (validation / gateway failure). Clear the overlay so the user can retry.
  window.jQuery(document.body).on('checkout_error', hideCheckoutRedirectOverlay);
}

// ─── Guest checkout → OTP auth (no page reload) ───────────────────────────────
// When a non-logged-in user clicks "Place Order", intercept WooCommerce's
// checkout_place_order event, read the billing phone, and open the auth modal at
// the OTP step. Once the modal reports a verified phone (chrz:checkout-verified)
// we stamp the proof token into the form and re-submit. The shopper is still a
// guest for that POST — so the checkout nonce stays valid — and the server
// creates/logs-in the account while the order is processed. No reload needed.
function setHiddenField(form, name, value) {
  let input = form.querySelector(`input[name="${name}"]`);
  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }
  input.value = value;
}

function initCheckoutAuth() {
  // Logged-in shoppers never need the guest OTP flow. Detect via WordPress's
  // `logged-in` body class so this works even when the auth assets (window.ChrzAuth)
  // are not loaded on the checkout page for authenticated users.
  if (document.body.classList.contains('logged-in')) return;
  if (!window.jQuery) return;

  const $form = window.jQuery('form.woocommerce-checkout');
  if (!$form.length) return;

  const formEl = $form.get(0);
  let verified = false;

  // The modal verified the phone via OTP and handed back a proof token. Inject
  // it and trigger the order submission; the re-fired handler sees `verified`
  // and lets WooCommerce proceed normally.
  window.addEventListener('chrz:checkout-verified', (event) => {
    const { token, phone } = event.detail || {};
    if (!token) return;

    setHiddenField(formEl, 'chrz_verified_token', token);
    setHiddenField(formEl, 'chrz_verified_phone', phone || '');

    verified = true;
    // The phone is verified and the order is about to submit — surface the
    // gateway wait right away so the screen never looks frozen.
    showCheckoutRedirectOverlay();
    document.getElementById('place_order')?.click();
  });

  $form.on('checkout_place_order', function () {
    if (verified) return; // phone already verified — let WooCommerce submit

    const persianToLatin = (str) =>
      str
        .split('')
        .map((c) => {
          const p = {
            '۰': '0',
            '۱': '1',
            '۲': '2',
            '۳': '3',
            '۴': '4',
            '۵': '5',
            '۶': '6',
            '۷': '7',
            '۸': '8',
            '۹': '9',
          };
          const a = {
            '٠': '0',
            '١': '1',
            '٢': '2',
            '٣': '3',
            '٤': '4',
            '٥': '5',
            '٦': '6',
            '٧': '7',
            '٨': '8',
            '٩': '9',
          };
          return p[c] ?? a[c] ?? c;
        })
        .join('');

    const rawPhone = document.querySelector('#billing_phone')?.value?.trim() ?? '';

    let phone = persianToLatin(rawPhone).replace(/\D/g, '');
    if (phone.startsWith('98') && phone.length === 12) phone = '0' + phone.slice(2);
    if (!phone.startsWith('0') && phone.length === 10) phone = '0' + phone;

    // Only intercept when we have a valid Iranian mobile number.
    // For an empty or malformed phone let WooCommerce's server validation run.
    if (!/^09[0-9]{9}$/.test(phone)) return;

    window.dispatchEvent(new CustomEvent('chrz:checkout-auth', { detail: { phone } }));

    return false; // prevent WooCommerce from proceeding with the AJAX order
  });
}

function init() {
  initQuantitySteppers();
  initCouponBar();
  initCheckoutAuth();
  initCheckoutSubmitFeedback();

  // WooCommerce replaces the cart table over AJAX on coupon/qty updates.
  // Re-decorate the freshly injected markup when jQuery is available.
  if (window.jQuery) {
    window.jQuery(document.body).on('updated_wc_div updated_cart_totals', () => {
      initQuantitySteppers();
    });
    // The checkout order-review fragment (including the coupon bar) is
    // replaced after every coupon/remove/update request. Rebind the bar so a
    // shopper can apply a new code after removing the previous one.
    window.jQuery(document.body).on('updated_checkout', () => {
      initCouponBar();
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
